import { cloneElement, useCallback, useEffect, useRef } from 'react'
import clsx from 'clsx'
import { debounce } from 'lodash-es'

import { LoadingState } from 'Components/LoadingState/LoadingState'

import { PreviewColumnProps } from './PreviewColumn.types'
import { PreviewColumnFooter } from './PreviewColumnFooter'

export const PreviewColumn = (props: PreviewColumnProps) => {
  const {
    id,
    type,
    url,
    contentElement,
    headerElement,
    isOpen,
    isLoading,
    onClosePreviewColumn,
    disableFooterAskQuestion,
    renderCustomFooterButton,
  } = props

  const previewColumnRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)

  const updateFooterWidth = useCallback(() => {
    const { current: previewColumn } = previewColumnRef
    const { current: footer } = footerRef

    if (!previewColumn || !footer) {
      return
    }

    const { width: previewColumnWidth } = previewColumn.getBoundingClientRect()

    footer.style.width = `${previewColumnWidth}px`
  }, [])

  const updateContentHeight = useCallback(() => {
    const { current: previewColumn } = previewColumnRef
    const { current: header } = headerRef
    const { current: content } = contentRef

    if (!previewColumn || !header || !content) {
      return
    }

    const { height: previewColumnHeight } =
      previewColumn.getBoundingClientRect()
    const { height: headerHeight } = header.getBoundingClientRect()

    content.style.height = `${previewColumnHeight - headerHeight}px`
  }, [])

  useEffect(() => {
    updateFooterWidth()
    updateContentHeight()
  }, [isLoading, updateContentHeight, updateFooterWidth])

  useEffect(() => {
    const debouncedUpdateFooterWidth = debounce(updateFooterWidth, 10)
    const debouncedUpdateContentHeight = debounce(updateContentHeight, 10)

    window.addEventListener('resize', debouncedUpdateFooterWidth)
    window.addEventListener('resize', debouncedUpdateContentHeight)

    return () => {
      window.removeEventListener('resize', debouncedUpdateFooterWidth)
      window.removeEventListener('resize', debouncedUpdateContentHeight)
    }
  }, [updateContentHeight, updateFooterWidth])

  return (
    <>
      <div
        ref={previewColumnRef}
        className={clsx(
          'PreviewColumn',
          id && isOpen && 'PreviewColumn--is-open',
          disableFooterAskQuestion && 'PreviewColumn--no-qBox',
        )}
      >
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center w-100 h-100">
            <LoadingState />
          </div>
        ) : (
          <>
            {headerElement && cloneElement(headerElement, { ref: headerRef })}

            {contentElement &&
              cloneElement(contentElement, { ref: contentRef })}
          </>
        )}
      </div>

      <div className={clsx(id && isOpen && 'PreviewColumn-footer--is-open')}>
        <PreviewColumnFooter
          ref={footerRef}
          disableAskQuestion={disableFooterAskQuestion}
          id={id}
          isOpen={isOpen}
          onClosePreviewColumn={onClosePreviewColumn}
          renderCustomFooterButton={renderCustomFooterButton}
          type={type}
          url={url}
        />
      </div>
    </>
  )
}
